/**
 * The comparison and click-cycle rules behind `ResponsiveTable`'s sortable
 * headers.
 *
 * Kept apart from the component so they can be tested as plain functions —
 * the sort order of an empty cell or a date string is exactly the kind of
 * thing that is easy to get subtly wrong and invisible in a screenshot.
 */

export type SortDirection = 'asc' | 'desc';

/** Which column is sorted and which way. `null` means "the order the data arrived in". */
export interface SortState {
  key: string;
  direction: SortDirection;
}

/** Values a column can sort on. */
export type Sortable = string | number | boolean | Date | null | undefined;

const isEmpty = (v: Sortable) => v === null || v === undefined || v === '';

/**
 * Compare two cell values.
 *
 * Empty cells sort last in BOTH directions. Treating them as "smallest" would
 * fill the top of a descending sort with blanks, which is never what someone
 * clicking a header wants to see.
 *
 * Strings compare with `numeric: true`, so "DEV-9" precedes "DEV-10" rather
 * than following it, and case-insensitively, so "acme" and "Acme" group.
 */
export function compareValues(a: Sortable, b: Sortable): number {
  if (isEmpty(a) && isEmpty(b)) return 0;
  if (isEmpty(a)) return 1;
  if (isEmpty(b)) return -1;
  return compareNonEmpty(a, b);
}

function compareNonEmpty(a: Sortable, b: Sortable): number {
  if (a instanceof Date || b instanceof Date) {
    const an = a instanceof Date ? a.getTime() : new Date(a as string).getTime();
    const bn = b instanceof Date ? b.getTime() : new Date(b as string).getTime();
    // An unparseable date is not comparable; treat it as empty rather than
    // letting NaN make the whole sort order arbitrary.
    if (Number.isNaN(an) && Number.isNaN(bn)) return 0;
    if (Number.isNaN(an)) return 1;
    if (Number.isNaN(bn)) return -1;
    return an - bn;
  }
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Compare for a given direction.
 *
 * Descending is NOT simply the negation of ascending: negating would invert
 * the nulls-last rule and fill the top of a descending sort with blanks. Only
 * the comparison between two present values flips; where a value is missing is
 * a property of the value, not of the direction.
 */
export function compareForDirection(a: Sortable, b: Sortable, direction: SortDirection): number {
  if (isEmpty(a) && isEmpty(b)) return 0;
  if (isEmpty(a)) return 1;
  if (isEmpty(b)) return -1;
  const base = compareNonEmpty(a, b);
  return direction === 'asc' ? base : -base;
}

/**
 * The click cycle: ascending, then descending, then back to unsorted.
 *
 * The third state matters because these lists arrive in an order the API chose
 * deliberately — open tasks before done ones, newest first — and without it a
 * reader who sorts once can never get that back without reloading.
 */
export function nextSort(current: SortState | null, key: string): SortState | null {
  if (!current || current.key !== key) return { key, direction: 'asc' };
  if (current.direction === 'asc') return { key, direction: 'desc' };
  return null;
}

/**
 * The `?ordering=` value DRF's OrderingFilter expects, or `undefined` when
 * nothing is sorted so the caller can omit the parameter entirely and let the
 * ViewSet's own `ordering` default apply.
 */
export function orderingParam(
  sort: SortState | null,
  sortKeyFor: (key: string) => string | undefined = (k) => k
): string | undefined {
  if (!sort) return undefined;
  const field = sortKeyFor(sort.key);
  if (!field) return undefined;
  return sort.direction === 'desc' ? `-${field}` : field;
}
